import React from 'react';
import { useFeatureToggle } from '../../hooks/useFeatureToggle';

/**
 * Feature Toggle Component
 * Wraps components/fields to show/hide based on feature toggles
 *
 * Usage:
 * <FeatureToggle featureKey="agent_form_persona" level="component">
 *   <Input ... />
 * </FeatureToggle>
 *
 * <FeatureToggle featureKey="analytics_page" level="page" fallback={<UpgradeMessage />}>
 *   <AnalyticsPage />
 * </FeatureToggle>
 */
export function FeatureToggle({
  featureKey,
  level = 'component',
  children,
  fallback = null,
  defaultValue = true,
  checkPage = false,
  pagePath,
}) {
  const isEnabled = useFeatureToggle(featureKey, {
    level,
    defaultValue,
    checkPage,
    pagePath,
  });

  if (!isEnabled) {
    return fallback;
  }

  return children;
}

/**
 * Conditional Field Component
 * For form fields that should be hidden based on toggles
 */
export function ConditionalField({
  featureKey,
  level = 'component',
  children,
  defaultValue = true,
  className,
}) {
  const isEnabled = useFeatureToggle(featureKey, {
    level,
    defaultValue,
  });

  if (!isEnabled) {
    return null;
  }

  return <div className={className}>{children}</div>;
}

/**
 * Conditional Page Component
 * For entire pages that should be hidden based on toggles
 */
export function ConditionalPage({ featureKey, pagePath, children, fallback }) {
  const isEnabled = useFeatureToggle(featureKey, {
    level: 'page',
    defaultValue: true,
    checkPage: true,
    pagePath,
  });

  if (!isEnabled) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Feature Not Available
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              This feature is not available in your current plan.
            </p>
          </div>
        </div>
      )
    );
  }

  return children;
}

export default FeatureToggle;
